export type GroupTalliesScope = {
  scopeType: string;
  companyId: number | null;
  groupId: number | null;
};

export type GroupTalliesGroup = {
  id: number;
  name: string;
  slug: string;
  kind: string | null;
};

export type GroupTalliesPeriod = {
  id: string;
  label: string;
  published: boolean;
  periodStart: string | null;
  periodEnd: string | null;
  periodType: string | null;
} | null;

export type GroupTalliesAvailablePeriod = {
  id: string;
  label: string;
  published: boolean;
  periodStart: string | null;
  periodEnd: string | null;
  periodType: string | null;
  isSelected: boolean;
};

export type GroupTalliesFreshness = {
  status: string;
  isStale: boolean;
  asOf: string | null;
  latestPeriodId: string | null;
  latestPeriodLabel: string | null;
  reasonCode: string | null;
  message: string;
};

export type GroupTalliesAllowedFilterOption = {
  value: string;
  label: string;
};

export type GroupTalliesAllowedFilter = {
  filterKey: string;
  label: string;
  options: GroupTalliesAllowedFilterOption[];
};

export type GroupTalliesPolicy = {
  policyScope: string;
  policyId: number | null;
  phase: string;
  defaultPeriodWindowMode: string;
  defaultPrivacyMode: string;
  status: string;
};

export type GroupTalliesPrivacy = {
  minimumCohortSize: number;
  anonymizedByDefault: boolean;
  namedComparisonsEnabledByDefault: boolean;
  namedComparisonEligible: boolean;
};

export type GroupTalliesMode = {
  modeKey: string;
  modeLabel: string;
  isDefault: boolean;
  allowedFilters: string[];
  minimumGroupSize: number;
  namedResultsAllowed: boolean;
};

export type GroupTalliesSuppressionReason = {
  code: string;
  message: string;
};

export type GroupTalliesNormalizationSummary = {
  valueSource: string;
  inputs: string[];
};

export type GroupTalliesWindowSummary = {
  periodType: string | null;
  periodStart: string | null;
  periodEnd: string | null;
};

export type GroupTalliesDimension = {
  dimensionKey: string;
  label: string;
  unitLabel: string;
  aggregationMethod: string;
  personValue: number | null;
  groupValue: number | null;
  cohortValue: number | null;
  historicalValue: number | null;
  targetValue: number | null;
  varianceValue: number | null;
  variancePercent: number | null;
  severity: string;
  normalizationSummary: GroupTalliesNormalizationSummary;
  comparisonBasis: string;
  baselineType: string;
  windowSummary: GroupTalliesWindowSummary;
  cohortSize: number;
  suppressed: boolean;
  suppressionReason: GroupTalliesSuppressionReason | null;
  comparisonGroupLabel: string | null;
  comparisonGroupNamed: boolean;
};

export type GroupTalliesCard = {
  cardKey: string;
  modeKey: string;
  title: string;
  subtitle: string;
  policyScope: string;
  benchmarkRuleId: number | null;
  comparisonGroupLabel: string | null;
  comparisonGroupNamed: boolean;
  eligibleViewerActions: string[];
  suppressionReason: GroupTalliesSuppressionReason | null;
};

export type GroupTalliesResponse = {
  resolvedScope: GroupTalliesScope;
  selectedGroup: GroupTalliesGroup;
  selectedPeriod: GroupTalliesPeriod;
  availablePeriods: GroupTalliesAvailablePeriod[];
  freshness: GroupTalliesFreshness;
  policy: GroupTalliesPolicy;
  privacy: GroupTalliesPrivacy;
  availableModes: GroupTalliesMode[];
  appliedFilters: Record<string, string>;
  allowedFilterMetadata: GroupTalliesAllowedFilter[];
  dimensions: GroupTalliesDimension[];
  comparisonCards: GroupTalliesCard[];
  suppressedCards: GroupTalliesCard[];
};

type RawGroupTalliesScope = {
  scope_type: string;
  company_id: number | null;
  group_id: number | null;
};

type RawGroupTalliesGroup = {
  id: number;
  name: string;
  slug: string;
  kind: string | null;
};

type RawGroupTalliesPeriod = {
  id: string;
  label: string;
  published: boolean;
  period_start: string | null;
  period_end: string | null;
  period_type: string | null;
} | null;

type RawGroupTalliesAvailablePeriod = {
  id: string;
  label: string;
  published: boolean;
  period_start: string | null;
  period_end: string | null;
  period_type: string | null;
  is_selected: boolean;
};

type RawGroupTalliesFreshness = {
  status: string;
  is_stale: boolean;
  as_of: string | null;
  latest_period_id: string | null;
  latest_period_label: string | null;
  reason_code: string | null;
  message: string;
};

type RawGroupTalliesAllowedFilterOption = {
  value: string;
  label: string;
};

type RawGroupTalliesAllowedFilter = {
  filter_key: string;
  label: string;
  options: RawGroupTalliesAllowedFilterOption[];
};

type RawGroupTalliesPolicy = {
  policy_scope: string;
  policy_id: number | null;
  phase: string;
  default_period_window_mode: string;
  default_privacy_mode: string;
  status: string;
};

type RawGroupTalliesPrivacy = {
  minimum_cohort_size: number;
  anonymized_by_default: boolean;
  named_comparisons_enabled_by_default: boolean;
  named_comparison_eligible: boolean;
};

type RawGroupTalliesMode = {
  mode_key: string;
  mode_label: string;
  is_default: boolean;
  allowed_filters: string[];
  minimum_group_size: number;
  named_results_allowed: boolean;
};

type RawGroupTalliesSuppressionReason = {
  code: string;
  message: string;
};

type RawGroupTalliesNormalizationSummary = {
  value_source: string;
  inputs: string[];
};

type RawGroupTalliesWindowSummary = {
  period_type: string | null;
  period_start: string | null;
  period_end: string | null;
};

type RawGroupTalliesDimension = {
  dimension_key: string;
  label: string;
  unit_label: string;
  aggregation_method: string;
  person_value: number | null;
  group_value: number | null;
  cohort_value: number | null;
  historical_value: number | null;
  target_value: number | null;
  variance_value: number | null;
  variance_percent: number | null;
  severity: string;
  normalization_summary: RawGroupTalliesNormalizationSummary;
  comparison_basis: string;
  baseline_type: string;
  window_summary: RawGroupTalliesWindowSummary;
  cohort_size: number;
  suppressed: boolean;
  suppression_reason: RawGroupTalliesSuppressionReason | null;
  comparison_group_label: string | null;
  comparison_group_named: boolean;
};

type RawGroupTalliesCard = {
  card_key: string;
  mode_key: string;
  title: string;
  subtitle: string;
  policy_scope: string;
  benchmark_rule_id: number | null;
  comparison_group_label: string | null;
  comparison_group_named: boolean;
  eligible_viewer_actions: string[];
  suppression_reason: RawGroupTalliesSuppressionReason | null;
};

type RawGroupTalliesResponse = {
  resolved_scope: RawGroupTalliesScope;
  selected_group: RawGroupTalliesGroup;
  selected_period: RawGroupTalliesPeriod;
  available_periods: RawGroupTalliesAvailablePeriod[];
  freshness: RawGroupTalliesFreshness;
  policy: RawGroupTalliesPolicy;
  privacy: RawGroupTalliesPrivacy;
  available_modes: RawGroupTalliesMode[];
  applied_filters: Record<string, string>;
  allowed_filter_metadata: RawGroupTalliesAllowedFilter[];
  dimensions: RawGroupTalliesDimension[];
  comparison_cards: RawGroupTalliesCard[];
  suppressed_cards: RawGroupTalliesCard[];
};

const readJson = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    throw new Error(
      response.status === 403
        ? 'You do not have access to the requested department tallies scope.'
        : response.status === 422
          ? 'The selected Department Tallies options are not available for the current policy.'
          : `Department Tallies could not be loaded (${response.status}).`
    );
  }

  return response.json() as Promise<T>;
};

const mapSuppressionReason = (
  reason: RawGroupTalliesSuppressionReason | null
): GroupTalliesSuppressionReason | null =>
  reason
    ? {
        code: reason.code,
        message: reason.message,
      }
    : null;

const mapCard = (card: RawGroupTalliesCard): GroupTalliesCard => ({
  cardKey: card.card_key,
  modeKey: card.mode_key,
  title: card.title,
  subtitle: card.subtitle,
  policyScope: card.policy_scope,
  benchmarkRuleId: card.benchmark_rule_id,
  comparisonGroupLabel: card.comparison_group_label,
  comparisonGroupNamed: card.comparison_group_named,
  eligibleViewerActions: card.eligible_viewer_actions,
  suppressionReason: mapSuppressionReason(card.suppression_reason),
});

const mapDimension = (dimension: RawGroupTalliesDimension): GroupTalliesDimension => ({
  dimensionKey: dimension.dimension_key,
  label: dimension.label,
  unitLabel: dimension.unit_label,
  aggregationMethod: dimension.aggregation_method,
  personValue: dimension.person_value,
  groupValue: dimension.group_value,
  cohortValue: dimension.cohort_value,
  historicalValue: dimension.historical_value,
  targetValue: dimension.target_value,
  varianceValue: dimension.variance_value,
  variancePercent: dimension.variance_percent,
  severity: dimension.severity,
  normalizationSummary: {
    valueSource: dimension.normalization_summary.value_source,
    inputs: dimension.normalization_summary.inputs,
  },
  comparisonBasis: dimension.comparison_basis,
  baselineType: dimension.baseline_type,
  windowSummary: {
    periodType: dimension.window_summary.period_type,
    periodStart: dimension.window_summary.period_start,
    periodEnd: dimension.window_summary.period_end,
  },
  cohortSize: dimension.cohort_size,
  suppressed: dimension.suppressed,
  suppressionReason: mapSuppressionReason(dimension.suppression_reason),
  comparisonGroupLabel: dimension.comparison_group_label,
  comparisonGroupNamed: dimension.comparison_group_named,
});

export const fetchGroupTallies = async (query: {
  group: string;
  company?: string | null;
  period?: string | null;
  periodMode?: string | null;
  periodValue?: string | null;
  periodStart?: string | null;
  periodEnd?: string | null;
  mode?: string | null;
  compareTo?: string | null;
  schedulerFilters?: boolean;
  date?: string | null;
  location?: string | null;
  physician?: string | null;
  service?: string | null;
  role?: string | null;
  shift?: string | null;
}): Promise<GroupTalliesResponse> => {
  const params = new URLSearchParams();
  params.set('group', query.group);
  if (query.company) params.set('company', query.company);

  if (query.period) {
    params.set('period', query.period);
  }

  if (query.periodMode) params.set('period_mode', query.periodMode);
  if (query.periodValue) params.set('period_value', query.periodValue);
  if (query.periodStart) params.set('period_start', query.periodStart);
  if (query.periodEnd) params.set('period_end', query.periodEnd);

  if (query.mode) {
    params.set('mode', query.mode);
  }

  if (query.compareTo) {
    params.set('compare_to', query.compareTo);
  }

  if (query.schedulerFilters) params.set('scheduler_filters', '1');

  (['date', 'location', 'physician', 'service', 'role', 'shift'] as const).forEach((key) => {
    if (query[key]) params.set(key, query[key]);
  });

  const payload = await readJson<RawGroupTalliesResponse>(
    await fetch(`/api/v1/secure/my-schedule/group-tallies?${params.toString()}`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
  );

  return {
    resolvedScope: {
      scopeType: payload.resolved_scope.scope_type,
      companyId: payload.resolved_scope.company_id,
      groupId: payload.resolved_scope.group_id,
    },
    selectedGroup: {
      id: payload.selected_group.id,
      name: payload.selected_group.name,
      slug: payload.selected_group.slug,
      kind: payload.selected_group.kind,
    },
    selectedPeriod: payload.selected_period
      ? {
          id: payload.selected_period.id,
          label: payload.selected_period.label,
          published: payload.selected_period.published,
          periodStart: payload.selected_period.period_start,
          periodEnd: payload.selected_period.period_end,
          periodType: payload.selected_period.period_type,
        }
      : null,
    availablePeriods: payload.available_periods.map((period) => ({
      id: period.id,
      label: period.label,
      published: period.published,
      periodStart: period.period_start,
      periodEnd: period.period_end,
      periodType: period.period_type,
      isSelected: period.is_selected,
    })),
    freshness: {
      status: payload.freshness.status,
      isStale: payload.freshness.is_stale,
      asOf: payload.freshness.as_of,
      latestPeriodId: payload.freshness.latest_period_id,
      latestPeriodLabel: payload.freshness.latest_period_label,
      reasonCode: payload.freshness.reason_code,
      message: payload.freshness.message,
    },
    policy: {
      policyScope: payload.policy.policy_scope,
      policyId: payload.policy.policy_id,
      phase: payload.policy.phase,
      defaultPeriodWindowMode: payload.policy.default_period_window_mode,
      defaultPrivacyMode: payload.policy.default_privacy_mode,
      status: payload.policy.status,
    },
    privacy: {
      minimumCohortSize: payload.privacy.minimum_cohort_size,
      anonymizedByDefault: payload.privacy.anonymized_by_default,
      namedComparisonsEnabledByDefault: payload.privacy.named_comparisons_enabled_by_default,
      namedComparisonEligible: payload.privacy.named_comparison_eligible,
    },
    availableModes: payload.available_modes.map((mode) => ({
      modeKey: mode.mode_key,
      modeLabel: mode.mode_label,
      isDefault: mode.is_default,
      allowedFilters: mode.allowed_filters,
      minimumGroupSize: mode.minimum_group_size,
      namedResultsAllowed: mode.named_results_allowed,
    })),
    appliedFilters: payload.applied_filters,
    allowedFilterMetadata: payload.allowed_filter_metadata.map((filter) => ({
      filterKey: filter.filter_key,
      label: filter.label,
      options: filter.options.map((option) => ({
        value: option.value,
        label: option.label,
      })),
    })),
    dimensions: payload.dimensions.map(mapDimension),
    comparisonCards: payload.comparison_cards.map(mapCard),
    suppressedCards: payload.suppressed_cards.map(mapCard),
  };
};
